// eslint-disable-next-line
import React, { useEffect, useCallback } from 'react';

import { GET_ONLY_ROOMS, GET_STUDENTS_PAGINATED_FOR_ROOM } from '../../../../../apollo/queries';
import { TDTKQuery } from '../../../../../apollo/types';
import { useLazyQuery } from '@apollo/client/react';
import { useStateValue, useStateDispatch } from '../../../../../context/AppContext';
import { errorHandler } from '../../../../common/network/QueryComponent';
import { sortItems } from '../../../../../utils/common';

import RoomChooser from '../../../../nonDigital/students/bulk/RoomChooser';
import groupBy from 'lodash/groupBy';
import { fetchMoreStudents } from '../../../../../utils/pagination';
import SpinnerWrapper from '../../../../ui/loading/SpinnerWrapper';

interface ModalChangeRoomBodyProps {
  // eslint-disable-next-line
  student?: any;
  // eslint-disable-next-line
  onChange?: (newRoom: any) => void;
}
export default function ModalChangeRoomBody({
  student = {},
  // eslint-disable-next-line
  onChange = (newRoom: any) => {},
}: ModalChangeRoomBodyProps) {
  const dispatch = useStateDispatch();

  const { user, orgEvent } = useStateValue();

  const [getOnlyRooms, { data: roomsData, loading: loadingRooms, error: errorRooms }] = useLazyQuery(GET_ONLY_ROOMS, {
    variables: { input: { siteId: orgEvent?.siteId || '', eventId: orgEvent?.eventId || '' } },
  });

  const [
    getStudentsForRoom,
    {
      data: studentData,
      fetchMore: fetchMoreStudentsForRoom,
      loading: loadingStudentsForRoom,
      error: errorStudentsForRoom,
    },
  ] = useLazyQuery(GET_STUDENTS_PAGINATED_FOR_ROOM, {
    variables: { input: {} },
  });

  useEffect(() => {
    getOnlyRooms({ variables: { input: { siteId: orgEvent?.siteId || '', eventId: orgEvent?.eventId || '' } } });
    getStudentsForRoom({ variables: { input: {} } });
    // eslint-disable-next-line
  }, []);

  const fetchMoreCallBack = useCallback(async () => {
    const options = fetchMoreStudents(studentData);
    if (options) {
      await fetchMoreStudentsForRoom(options);
    }
    // eslint-disable-next-line
  }, [studentData, fetchMoreStudents, fetchMoreStudentsForRoom]);

  useEffect(() => {
    fetchMoreCallBack();
    // eslint-disable-next-line
  }, [studentData]);

  const rooms = roomsData?.viewer?.site?.rooms || [];

  if (errorStudentsForRoom) {
    const query = {
      kind: 'GetStudentPaginatedForRoom',
      specification: GET_STUDENTS_PAGINATED_FOR_ROOM,
      variables: { input: {} },
      // eslint-disable-next-line
    } as TDTKQuery<any>;
    errorHandler(dispatch, query, true, studentData, studentData, errorStudentsForRoom);
  }
  if (errorRooms) {
    const query = {
      kind: 'GetRooms',
      specification: GET_ONLY_ROOMS,
      variables: { input: { siteId: orgEvent?.siteId || '', eventId: orgEvent?.eventId || '' } },
      // eslint-disable-next-line
    } as TDTKQuery<any>;
    errorHandler(dispatch, query, true, roomsData, roomsData, errorRooms);
  }

  if (loadingRooms || loadingStudentsForRoom) {
    return <SpinnerWrapper />;
  }

  if (rooms.length) {
    const allStudents = (studentData?.paginatedStudentForRoom?.students || []).filter(Boolean);
    const lastKey = studentData?.paginatedStudentForRoom?.lastKey;
    if (lastKey) {
      return <SpinnerWrapper />;
    }
    const studentsByRoom = groupBy(allStudents, 'room.id');
    // eslint-disable-next-line
    let roomsWithStudents = rooms.map((room: any) => ({
      ...room,
      students: studentsByRoom[room.id] || [],
    }));

    roomsWithStudents = sortItems(
      roomsWithStudents,
      [
        {
          name: 'title',
          order: 'asc',
        },
      ],
      'natural'
    );
    return <RoomChooser rooms={[...roomsWithStudents]} students={[student]} onChange={onChange} user={user} />;
  }
  return <p>You can&rsquo;t make this change because there aren&rsquo;t enough available seats.</p>;
}
